import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { BsPlusLg, BsGripVertical } from "react-icons/bs";
import { capitalizeWords } from "Utils/utils";
import DocumentRow, { PARTICIPANT_DOC_GRID_CLASS } from "./DocumentRow";

const SortableDocumentRow = ({
  item,
  complianceType,
  lastErrorLocation,
  onSwitchChange,
  onScoreChange,
  onLastErrorChange,
  onDelete,
  onEdit,
  deleteLoadingId,
  showDateRequirements,
  onDateRequirementChange,
  mandatorySubtitle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    data: { item },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <DocumentRow
        item={item}
        complianceType={complianceType}
        lastErrorLocation={lastErrorLocation}
        onSwitchChange={onSwitchChange}
        onScoreChange={onScoreChange}
        onLastErrorChange={onLastErrorChange}
        dragHandleProps={{ attributes, listeners }}
        onDelete={onDelete}
        onEdit={onEdit}
        deleteLoading={deleteLoadingId === item.uuid}
        showDateRequirements={showDateRequirements}
        onDateRequirementChange={onDateRequirementChange}
        mandatorySubtitle={mandatorySubtitle}
      />
    </div>
  );
};

const ParticipantComplianceDocumentsSection = ({
  items = [],
  complianceType = "document",
  lastErrorLocation,
  onSwitchChange,
  onScoreChange,
  onLastErrorChange,
  onAddDocument,
  onDeleteDocument,
  onEditDocument,
  deleteDocumentLoadingId,
  showDateRequirements = true,
  onDateRequirementChange,
  mandatorySubtitle = "Mandatory for all participants",
  emptyMessage = "No documents yet. Add your first compliance document below.",
}) => {
  return (
    <SortableContext
      items={items.map((i) => i.id)}
      strategy={verticalListSortingStrategy}
    >
      <div className="divide-y divide-gray-100 bg-white min-h-[40px]">
        {items.length > 0 && (
          <div
            className={`hidden lg:grid ${PARTICIPANT_DOC_GRID_CLASS} gap-x-4 items-center px-4 py-2 bg-gray-50/90 border-b border-gray-200 text-xs font-medium text-gray-500`}
          >
            <div aria-hidden="true" />
            <div>Document</div>
            <div className="text-center">Require issue date</div>
            <div className="text-center">Require expiry date</div>
            <div className="text-center">Mandatory</div>
            <div className="text-center">Percentage</div>
            <div aria-hidden="true" />
          </div>
        )}
        {items.length === 0 && (
          <p className="px-4 py-8 text-sm text-gray-500 text-center">
            {emptyMessage}
          </p>
        )}
        {items.map((item) => (
          <SortableDocumentRow
            key={item.id}
            item={item}
            complianceType={complianceType}
            lastErrorLocation={lastErrorLocation}
            onSwitchChange={onSwitchChange}
            onScoreChange={onScoreChange}
            onLastErrorChange={onLastErrorChange}
            onDelete={onDeleteDocument}
            onEdit={onEditDocument}
            deleteLoadingId={deleteDocumentLoadingId}
            showDateRequirements={showDateRequirements}
            onDateRequirementChange={onDateRequirementChange}
            mandatorySubtitle={mandatorySubtitle}
          />
        ))}
      </div>
      {onAddDocument && (
        <div className="flex justify-center py-3 px-4 border-t bg-gray-50/50 border-gray-100">
          <button
            type="button"
            onClick={() => onAddDocument()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors text-indigo-600 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100"
          >
            <BsPlusLg className="w-4 h-4" />
            Add Document
          </button>
        </div>
      )}
    </SortableContext>
  );
};

export const ParticipantComplianceDocumentDragOverlay = ({ activeItem }) => {
  if (!activeItem) return null;
  return (
    <div className="flex items-center gap-4 py-3 px-4 text-sm bg-white rounded-lg shadow-lg border-2 border-indigo-300 opacity-95 cursor-grabbing">
      <div className="flex-shrink-0 text-indigo-500">
        <BsGripVertical className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">
          {capitalizeWords(activeItem.name || "Document")}
        </p>
        <p className="text-xs text-gray-500">Drag to reorder</p>
      </div>
    </div>
  );
};

export default ParticipantComplianceDocumentsSection;
